import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import parse from "html-react-parser";
import {
	MdOutlineAccessTimeFilled,
	MdPerson,
	MdRemoveRedEye,
} from "react-icons/md";
import { toast } from "react-toastify";
import ShareSosmed from "./Share";
import KomentarSection from "./KomentarSection";
import { Helmet } from "react-helmet-async";

function Details() {
	const { slug } = useParams();
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchDetail = async () => {
			if (!slug) return;
			try {
				setLoading(true);
				const response = await axios.get(`/api_w/posts`, {
					params: { slug },
					headers: {
						db_name: process.env.REACT_APP_DB_NAME,
					},
				});

				// The API might return an array or a single object
				const data = response.data.data;
				if (Array.isArray(data)) {
					setDetail(data[0] || null);
				} else {
					setDetail(data || null);
				}
				setLoading(false);
			} catch (error) {
				console.error("Fetch error:", error);
				// Only toast once, not in interval
				toast.error(error.response?.data?.message || "Gagal memuat berita");
				setLoading(false);
			}
		};
		fetchDetail();
	}, [slug]);

	const formatDate = (dateString) => {
		const options = { year: "numeric", month: "long", day: "numeric" };
		return new Date(dateString).toLocaleDateString("id-ID", options);
	};

	const formatTime = (dateString) => {
		return new Date(dateString).toLocaleTimeString("id-ID", {
			hour: "numeric",
			minute: "numeric",
		});
	};

	const truncateDescription = (text, maxLength) => {
		if (!text) return "";
		const cleanText = text.replace(/<[^>]*>/g, "");
		return cleanText.length > maxLength
			? `${cleanText.slice(0, maxLength)}...`
			: cleanText;
	};

	const truncatedDescription = detail
		? truncateDescription(detail.description, 160)
		: "";

	if (loading) {
		return (
			<div className="animate-pulse space-y-6">
				<div className="h-8 w-3/4 bg-gray-200 rounded" />
				<div className="flex gap-4">
					<div className="h-4 w-24 bg-gray-200 rounded" />
					<div className="h-4 w-24 bg-gray-200 rounded" />
				</div>
				<div className="h-64 w-full bg-gray-200 rounded-2xl" />
				<div className="space-y-3">
					<div className="h-4 w-full bg-gray-200 rounded" />
					<div className="h-4 w-full bg-gray-200 rounded" />
					<div className="h-4 w-2/3 bg-gray-200 rounded" />
				</div>
			</div>
		);
	}

	if (!detail) {
		return (
			<div className="text-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-300">
				<div className="text-gray-400 mb-4">
					<svg
						className="w-16 h-16 mx-auto"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1}
							d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l4 4v10a2 2 0 01-2 2z"
						/>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1}
							d="M14 3v5h5"
						/>
					</svg>
				</div>
				<h2 className="text-xl font-bold text-gray-800">
					Berita tidak ditemukan
				</h2>
				<p className="text-gray-500 mt-2">
					Maaf, berita yang Anda cari tidak tersedia atau telah dihapus.
				</p>
				<button
					onClick={() => window.history.back()}
					className="mt-6 text-green-600 font-semibold hover:underline"
				>
					Kembali ke Berita
				</button>
			</div>
		);
	}

	return (
		<div className="relative flex flex-col">
			<Helmet>
				<title>{`Jogja Madrasah Digital | ${detail.title}`}</title>
				<meta name="description" content={truncatedDescription} />
				<meta name="keywords" content="Jogja Madrasah Digital" />
				<meta
					property="og:title"
					content={`Jogja Madrasah Digital | ${detail.title}`}
				/>
				<meta property="og:description" content={truncatedDescription} />
				<meta property="og:image" content={detail.image} />
				<meta property="og:image:size" content="300" />
				<meta property="og:url" content={window.location.href} />
				<meta property="og:type" content={detail.PostCategory?.name} />
				<meta name="twitter:card" content="summary" />
				<meta
					name="twitter:title"
					content={`Jogja Madrasah Digital | ${detail.title}`}
				/>
				<meta name="twitter:description" content={truncatedDescription} />
				<meta name="twitter:image" content={detail.image} />
				<meta name="twitter:site" content={window.location.href} />
			</Helmet>

			{/* Article Title */}
			<h1 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight">
				{detail.title}
			</h1>

			{/* Meta Info */}
			<div className="flex flex-wrap items-center gap-4 mt-4 mb-6">
				<span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
					<MdOutlineAccessTimeFilled className="w-4 h-4 text-green-600" />
					{formatDate(detail.date)}
				</span>
				{detail.date && (
					<span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
						<svg
							className="w-4 h-4 text-green-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						{formatTime(detail.date)} WIB
					</span>
				)}
				{detail.User?.name && (
					<span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
						<MdPerson className="w-4 h-4 text-green-600" />
						{detail.User?.name}
					</span>
				)}
				{detail.views && (
					<span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
						<MdRemoveRedEye className="w-4 h-4 text-green-600" />
						{detail.views} views
					</span>
				)}
			</div>

			{/* Main Image */}
			<div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
				<img
					className="w-full h-auto"
					src={detail.image || "https://placehold.co/600x400?text=No-Image"}
					alt={detail.title}
				/>
			</div>

			{/* Article Content */}
			<div className="prose prose-green max-w-none text-gray-700 leading-relaxed text-justify">
				{typeof detail.description === "string"
					? parse(detail.description)
					: detail.description}
			</div>

			{/* Gallery Section */}
			<div className="mt-12">
				<h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
					<svg
						className="w-5 h-5 text-green-600"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
						/>
					</svg>
					Galeri Foto
				</h3>
				<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
					{[detail.image, detail.image, detail.image].map((img, idx) => (
						<div
							key={idx}
							className="aspect-video rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
						>
							<img
								src={img || "https://placehold.co/600x400?text=Gallery"}
								alt="Gallery"
								className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
							/>
						</div>
					))}
				</div>
			</div>

			{/* Share */}
			<ShareSosmed />

			{/* Comments */}
			<KomentarSection />
		</div>
	);
}

export default Details;
